import { defineStore, StoreDefinition } from 'pinia';
import { EmployeeStore, useEmployeeStore } from './employees';
import { AreaStore, useAreaStore } from './areas';
import { LocationStore, useLocationStore } from './locations';
import { TimesheetStore, useTimesheetStore } from './timesheets';
import { RosterStore, useRosterStore } from './rosters';
import {
  EmployeeWithTimesheetStore,
  useEmployeeWithTimesheetsStore,
} from './employeesWithTimesheets/employeesWithTimesheets';
import { KioskConfigStore, useKioskConfigStore } from './kioskConfig';
import { useAnalyticsService } from '@/lib/util/analytics/useAnalyticsService';
import MissingKioskInstallationIdError from '@/lib/util/error/MissingKioskInstallationIdError';
import {
  getKioskInstallationId,
  resetCookieKioskInstallationId,
  getKioskSource,
} from '@/lib/util/auth';
import KioskConnectionMode from '@/lib/request/types/KioskConnectionMode';

export enum KioskSourceKeys {
  'kiosk_view' = 'KioskView',
  'kiosk_list' = 'KioskList',
  'kiosk_banner' = 'KioskBanner',
}

enum KioskSource {
  KioskView = 'kiosk_view',
  KioskList = 'kiosk_list',
  KioskBanner = 'kiosk_banner',
}

const eventSources = {
  [KioskSource.KioskView]: 'Run kiosk',
  [KioskSource.KioskList]: 'Launch kiosk mode',
  [KioskSource.KioskBanner]: 'Try new web time clock',
  LoadKiosk: 'Load kiosk',
};

const eventScreens = {
  [KioskSource.KioskView]: 'KIOSK_SETTINGS',
  [KioskSource.KioskList]: 'KIOSK_LIST',
  [KioskSource.KioskBanner]: 'PEOPLE',
};
interface EventPayload {
  name: string;
  source: string;
  screen: string;
  errorMessage?: string;
}

interface EventDetails {
  kioskId?: number;
  error?: { message: string };
  authType?: string;
  isAllLocations?: boolean;
  locationIds?: Array<number>;
}

function trackKioskLoad({ name, source, screen, errorMessage }: EventPayload) {
  const kioskConfigStore = useKioskConfigStore();
  const analytics = useAnalyticsService();
  const details: EventDetails = {};

  if (Object.keys(kioskConfigStore.data).length) {
    details.kioskId = kioskConfigStore.data.KioskId;
    details.authType = kioskConfigStore.getAuthenticationMode;

    if (kioskConfigStore.data.KioskAppConfigs.ENABLE_MULTI_LOCATIONS) {
      details.isAllLocations = true;
      details.locationIds = useLocationStore().data.map(
        (location) => location.Id
      );
    } else {
      details.isAllLocations = false;
      details.locationIds = [kioskConfigStore.data.KioskCompanyId];
    }
  }

  if (errorMessage) {
    details.error = { message: errorMessage };
  }

  analytics.track(
    { name, screen },
    Object.keys(details).length ? { source, kiosk: { details } } : { source }
  );
}

interface I18next {
  isInitialized: boolean;
}

export interface RootStore {
  employeeStore: EmployeeStore;
  areaStore: AreaStore;
  locationStore: LocationStore;
  timesheetStore: TimesheetStore;
  rosterStore: RosterStore;
  kioskConfigStore: KioskConfigStore;
  employeeWithTimesheetsStore: EmployeeWithTimesheetStore;
  i18next: I18next;
}

export const useRootStore: StoreDefinition = defineStore({
  id: 'root',
  state: (): RootStore => ({
    employeeStore: useEmployeeStore(),
    areaStore: useAreaStore(),
    locationStore: useLocationStore(),
    timesheetStore: useTimesheetStore(),
    rosterStore: useRosterStore(),
    kioskConfigStore: useKioskConfigStore(),
    employeeWithTimesheetsStore: useEmployeeWithTimesheetsStore(),
    i18next: {
      isInitialized: false,
    },
  }),
  getters: {
    canInitialize(): () => boolean {
      return () => {
        try {
          getKioskInstallationId();
        } catch (e) {
          if (e instanceof MissingKioskInstallationIdError) {
            return false;
          }
        }

        return true;
      };
    },
    isInitializing: (state: any) => {
      //TODO replace any
      return () =>
        state.employeeStore.status.isLoading ||
        state.areaStore.status.isLoading ||
        state.locationStore.status.isLoading ||
        state.kioskConfigStore.status.isLoading ||
        !state.i18next.isInitialized;
    },
    getInitializeError: (state: any) => {
      //TODO replace any
      return () =>
        state.employeeStore.errors.loadingError ||
        state.areaStore.errors.loadingError ||
        state.locationStore.errors.loadingError ||
        state.timesheetStore.errors.loadingError ||
        state.kioskConfigStore.errors.loadingError;
    },
  },
  actions: {
    async load() {
      const analytics = useAnalyticsService();

      const kioskSource = getKioskSource();
      const eventSource =
        eventSources[kioskSource as keyof typeof KioskSourceKeys];
      const eventScreen =
        eventScreens[kioskSource as keyof typeof KioskSourceKeys];

      try {
        if (kioskSource) {
          trackKioskLoad({
            name: 'KIOSK_LOAD_REQUESTED',
            source: eventSource,
            screen: eventScreen,
          });
        }

        await this.kioskConfigStore.load();
        await Promise.all([
          this.employeeStore.load(),
          this.timesheetStore.load(),
          this.rosterStore.load(),
          this.areaStore.load(),
          this.locationStore.load(),
        ]);

        if (
          this.kioskConfigStore.data.KioskConnectionMode ==
          KioskConnectionMode.Cookie
        ) {
          resetCookieKioskInstallationId();
        }

        await this.employeeWithTimesheetsStore.load();

        if (kioskSource) {
          trackKioskLoad({
            name: 'KIOSK_LOAD_SUCCESS_ACKNOWLEDGED',
            source: eventSource,
            screen: eventScreen,
          });
          analytics.track(
            { name: 'LOGIN_INTO_INSTALLATION_COMPLETED', screen: 'LOGIN' },
            { source: eventSources.LoadKiosk }
          );
        }
      } catch (e) {
        if (e instanceof Error) {
          if (kioskSource) {
            trackKioskLoad({
              name: 'KIOSK_LOAD_FAILURE_ACKNOWLEDGED',
              source: eventSource,
              screen: eventScreen,
              errorMessage: e.message,
            });
          }
          throw e;
        }
      }
    },
    completeI18nextInitialization() {
      this.i18next.isInitialized = true;
    },
  },
});
